/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


// Product card: update the price to the selected color's variant price on swatch change
// (images are switched by theme.js; each swatch carries its price in a <template class="color-swatch__price">)
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input.matches || !input.matches('.product-item-meta__swatch-list .color-swatch__radio')) return;

  const template = input.closest('.color-swatch')?.querySelector('template.color-swatch__price');
  const priceList = input.closest('product-item')?.querySelector('.product-item-meta__price-list-container .price-list');
  if (!template || !priceList) return;

  // Replace only the price spans; keep the price label and unit price as they are
  priceList.querySelectorAll(':scope > .price:not(.price--block)').forEach((price) => price.remove());

  const label = priceList.querySelector(':scope > .cart-price-lable');
  const newPrice = template.content.cloneNode(true);
  if (label) {
    label.after(newPrice);
  } else {
    priceList.prepend(newPrice);
  }
});

// Product card: on swatch change, hover shows the selected color's own 2nd image (matched by SKU in Liquid).
// Colors without one get no hover. Only the active hover image carries "product-item__secondary-image".
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input.matches || !input.matches('.product-item-meta__swatch-list .color-swatch__radio')) return;

  const imageWrapper = input.closest('product-item')?.querySelector('.product-item__image-wrapper');
  if (!imageWrapper) return;

  const hoverImages = imageWrapper.querySelectorAll('.product-item__hover-image');
  if (!hoverImages.length) return; // product doesn't use swatch hover images (theme default hover is kept)

  const hoverMediaId = input.getAttribute('data-hover-media');
  let hasHoverImage = false;

  hoverImages.forEach((image) => {
    const isActive = hoverMediaId !== null && image.getAttribute('data-hover-media-id') === hoverMediaId;
    image.classList.toggle('product-item__secondary-image', isActive);
    hasHoverImage = hasHoverImage || isActive;
  });

  imageWrapper.classList.toggle('product-item__image-wrapper--multiple', hasHoverImage);
});

// Product card color swatch slider (prev/next arrows when more swatches than fit: 4 desktop / 3 mobile)
if (!window.customElements.get('product-swatch-slider')) {
  window.customElements.define('product-swatch-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.product-item-swatches__track');
      this.prevButton = this.querySelector('[data-direction="prev"]');
      this.nextButton = this.querySelector('[data-direction="next"]');
      if (!this.track) return;

      this.swatches = Array.from(this.track.querySelectorAll('.color-swatch'));
      this._layout = this._layout.bind(this);
      this._updateArrows = this._updateArrows.bind(this);
      this.prevButton.addEventListener('click', () => this._scroll(-1));
      this.nextButton.addEventListener('click', () => this._scroll(1));
      this.track.addEventListener('scroll', this._updateArrows, { passive: true });

      // After any scroll (swipe, focus, arrows), make sure the strip rests on a swatch edge
      this._align = this._align.bind(this);
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', this._align);
      } else {
        this.track.addEventListener('scroll', () => {
          clearTimeout(this._alignTimer);
          this._alignTimer = setTimeout(this._align, 150);
        }, { passive: true });
      }

      this.resizeObserver = new ResizeObserver(this._layout);
      this.resizeObserver.observe(this);

      this._initMouseDrag();

      this._layout();
      this._scrollToChecked();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    // Number of swatches in view: --swatch-visible (4 desktop / 3 mobile, set from product-item.liquid)
    _visibleCount() {
      return parseInt(getComputedStyle(this).getPropertyValue('--swatch-visible'), 10) || 4;
    }

    // Size each swatch and the track in whole px so exactly N swatches fit (no partial swatch at the edges)
    _layout() {
      const visible = this._visibleCount();
      this.classList.toggle('is-scrollable', this.swatches.length > visible);

      const rowWidth = this.getBoundingClientRect().width;
      if (!rowWidth) return;

      const style = getComputedStyle(this);
      const rowGap = parseFloat(style.columnGap) || 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const maxWidth = parseFloat(style.getPropertyValue('--swatch-width')) || 50;
      const arrowsWidth = this.prevButton.getBoundingClientRect().width + this.nextButton.getBoundingClientRect().width + rowGap * 2;
      const available = rowWidth - arrowsWidth;
      const itemWidth = Math.max(1, Math.min(maxWidth, Math.floor((available - gap * (visible - 1)) / visible)));

      this.step = itemWidth + gap;
      this.pageWidth = this.step * visible;
      this.style.setProperty('--swatch-item-width', `${itemWidth}px`);
      this.track.style.flex = `0 0 ${itemWidth * visible + gap * (visible - 1)}px`;
      this.track.style.maxWidth = 'none';
      this._align();
      this._updateArrows();
    }

    // Desktop: drag the swatches with the mouse like a finger swipe on mobile (touch keeps native scrolling).
    // A drag never selects a swatch; a plain click still does.
    _initMouseDrag() {
      const DRAG_THRESHOLD = 5;
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.classList.contains('is-scrollable')) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
        this.isDragging = false;
      });

      this.track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;

        const deltaX = event.clientX - startX;
        if (!this.isDragging) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
          this.isDragging = true;
          this.isAnimating = false;
          try { this.track.setPointerCapture(pointerId); } catch (error) {}
          this.track.style.scrollSnapType = 'none';
          this.classList.add('is-dragging');
        }

        this.track.scrollLeft = startScroll - deltaX;
      });

      const endDrag = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (!this.isDragging) return;

        // Swallow the click that follows a drag so the swatch under the mouse isn't selected
        // (removed shortly after in case the browser fires no click, so the next real click still works)
        const swallowClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        this.track.addEventListener('click', swallowClick, { capture: true, once: true });
        setTimeout(() => {
          this.track.removeEventListener('click', swallowClick, { capture: true });
          this.isDragging = false;
        }, 50);

        this.classList.remove('is-dragging');
        this._scrollToIndex(this._currentIndex());
      };

      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);
    }

    // Index of the first swatch currently in view
    _currentIndex() {
      return this.step ? Math.round(this.track.scrollLeft / this.step) : 0;
    }

    // Scroll to an exact swatch index. Scroll-snap is paused while moving: mobile browsers (iOS Safari especially)
    // otherwise fight the programmatic smooth scroll and stop on the wrong swatch.
    _scrollToIndex(index, smooth = true) {
      if (!this.step) return;

      const maxIndex = Math.max(0, this.swatches.length - this._visibleCount());
      this.targetIndex = Math.max(0, Math.min(maxIndex, index));
      const target = this.targetIndex * this.step;

      this.isAnimating = smooth;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'instant' });

      clearTimeout(this._snapTimer);
      this._snapTimer = setTimeout(() => {
        this.isAnimating = false;
        this.track.style.scrollSnapType = '';
        this._updateArrows();
      }, smooth ? 600 : 0);
    }

    // Arrows move one swatch at a time, never past the ends
    // (uses the target of a running scroll so quick repeated clicks each move one more swatch)
    _scroll(direction) {
      const from = this.isAnimating ? this.targetIndex : this._currentIndex();
      this._scrollToIndex(from + direction);
    }

    // After a swipe/focus scroll, settle on a swatch edge (skipped while an arrow scroll is running)
    _align() {
      if (!this.step || this.isAnimating || this.isDragging) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const target = Math.min(maxScroll, this._currentIndex() * this.step);
      if (Math.abs(target - this.track.scrollLeft) > 1) {
        this._scrollToIndex(this._currentIndex());
      }
    }

    _updateArrows() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.prevButton.disabled = this.track.scrollLeft <= 1;
      this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
    }

    // Bring the initially selected swatch into view only if it sits beyond the visible ones
    _scrollToChecked() {
      const checked = this.track.querySelector('.color-swatch__radio:checked');
      if (!checked) return;

      const index = this.swatches.indexOf(checked.closest('.color-swatch'));
      const visible = this._visibleCount();
      this._scrollToIndex(index >= visible ? Math.floor(index / visible) * visible : 0, false);
      this._updateArrows();
    }
  });
}

$( document ).ready(function() {

    // $('.tab-wrap > .tab:first-child').addClass('active');

    // $('.tab-wrap .tab').on('click', function () {
    //     $('.tab-wrap .tab').removeClass('active');
    //     $(this).addClass('active');
    // });

    // $('.tab > a').on('click', function () {
    //     var currentTitle = $(this);
    //     var currentAnchor = currentTitle.attr('value');
    //     var scrollContainer = document.getElementById('scroll-container'); 
    //     var scrollToTitle = document.getElementById(currentAnchor);
    //     scrollContainer.scrollTo({top: scrollToTitle.offsetTop, behavior: 'smooth'});
    //     console.log('Nice');
    // });

    $('.tooltip').tooltipster({
        animation: 'fade',
        theme: 'tooltipster-shadow',
        trigger: 'click',
        interactive: true,
        maxWidth: 500
    });
  
    // Select all divs with class 'product__thumbnail-item' and aria-current='true'
    var thumbnailDiv = document.querySelector('.product__thumbnail-item[aria-current="true"]');
    if(thumbnailDiv){
      var mediaId = thumbnailDiv.getAttribute('data-media-id');
      
      if(mediaId){
        var checkedDivMediainput = document.querySelector('input[data-onload-selected-media]');
        if(checkedDivMediainput){
          var checkedMediaId = checkedDivMediainput.getAttribute('data-onload-selected-media');
          // console.log(checkedMediaId);
          if(checkedMediaId){
            if(mediaId != checkedMediaId){
            	var clickDiv = document.querySelector('.product__thumbnail-item[data-media-id="' + checkedMediaId + '"]');
            	clickDiv.click();
            }
          }
        }
      }
    }
	
	if($("body").hasClass("template--product")) {
		setTimeout(() => {
		  shopifyPaymentTermsHeight();
		}, 1000);
		$(window).on("resize", function() {
		  shopifyPaymentTermsHeight();
		});
	}
});

$(window).on('load',function(){
  $('.product__price').show();
});

function shopifyPaymentTermsHeight() {
  var paymentTermsElem = $("[data-block-type='payment-terms'] .shopify-product-form shopify-payment-terms");
  if(paymentTermsElem.length > 0){
    var paymentTermsElemHeight = $(paymentTermsElem).height();
    if($(".product-meta").length > 0) {
      if($("[data-style]").length > 0) {
        $("[data-style]").remove();
      }
      $(".product-meta").after(`<style data-style> .product .product-meta [data-block-type="payment-terms"] .shopify-product-form{ min-height: ` + paymentTermsElemHeight + `px; } </style>`);
    }
  }
}